import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Shield,
  Car,
  Calendar,
  FileText,
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  Loader2,
  AlertTriangle,
  MapPin,
  User,
  Building,
  Wrench,
  Upload,
  Clock,
  DollarSign,
  Phone,
  Mail,
  XCircle,
  RefreshCw,
  Download,
  Eye,
  Plus,
  Paperclip,
  Trash2,
  Image,
  File
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import PortalLayout from '../components/PortalLayout';
import DocumentPreviewModal from '../components/DocumentPreviewModal';
import { getUserFriendlyError } from '../utils/errorMessages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status icon mapping
const getStatusIcon = (status) => {
  const icons = {
    draft: FileText,
    submitted: Clock,
    under_review: Eye,
    pending_documents: AlertCircle,
    in_appraisal: DollarSign,
    approved: CheckCircle,
    denied: XCircle,
    paid: CheckCircle,
    closed: FileText
  };
  return icons[status] || FileText;
};

// Status color mapping
const getStatusStyles = (status) => {
  const styles = {
    draft: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-300' },
    submitted: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
    under_review: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' },
    pending_documents: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
    in_appraisal: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
    approved: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
    denied: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
    paid: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300' },
    closed: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-300' }
  };
  return styles[status] || styles.draft;
};

// Currency formatting
const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  if (num % 1 === 0) {
    return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Date formatting
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

export default function ClaimDetailPage() {
  const navigate = useNavigate();
  const { claimNumber } = useParams();
  const { getToken, logout } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [claim, setClaim] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [userName, setUserName] = useState('');
  const [showUploader, setShowUploader] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(0);

  useEffect(() => {
    fetchClaimDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimNumber]);

  const fetchClaimDetails = async () => {
    const token = getToken();
    if (!token) {
      navigate('/portal/login');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch claim details and timeline in parallel
      const [claimRes, timelineRes, dashboardRes] = await Promise.all([
        fetch(`${API_URL}/api/claims/${claimNumber}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/claims/${claimNumber}/timeline`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/portal/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (claimRes.status === 401) {
        await logout();
        navigate('/portal/login');
        return;
      }

      if (claimRes.status === 404) {
        setError('Claim not found');
        return;
      }

      const claimData = await claimRes.json();
      const timelineData = await timelineRes.json();
      const dashboardData = await dashboardRes.json();

      if (claimData.success) {
        setClaim(claimData.claim);
      }
      if (timelineData.success) {
        setTimeline(timelineData.timeline);
      }
      if (dashboardData.policyholder) {
        setUserName(dashboardData.policyholder.first_name || 'Policyholder');
      }

    } catch (err) {
      setError(getUserFriendlyError(err.message));
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (files) => {
    const token = getToken();
    if (!token || !claimNumber) return;
    
    setUploading(true);
    setUploadError(null);
    
    const fileArray = Array.from(files);
    const maxDocuments = 10;
    const currentCount = claim?.documents?.length || 0;
    
    if (currentCount + fileArray.length > maxDocuments) {
      setUploadError(`Maximum ${maxDocuments} documents allowed per claim`);
      setUploading(false);
      return;
    }
    
    for (const file of fileArray) {
      // Validate file type
      const ext = '.' + file.name.toLowerCase().split('.').pop();
      const allowedExts = ['.jpg', '.jpeg', '.png', '.heic', '.heif', '.webp', '.pdf', '.doc', '.docx'];
      if (!allowedExts.includes(ext)) {
        setUploadError(`File type ${ext} not allowed`);
        continue;
      }
      
      // Validate file size (25 MB)
      if (file.size > 25 * 1024 * 1024) {
        setUploadError(`File ${file.name} exceeds 25 MB limit`);
        continue;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', 'other');
        
        const response = await fetch(`${API_URL}/api/documents/upload/${claimNumber}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Upload failed');
        }
        
      } catch (err) {
        setUploadError(err.message);
      }
    }
    
    setUploading(false);
    setShowUploader(false);
    // Refresh claim data
    fetchClaimDetails();
  };

  // Handle document delete
  const handleDeleteDocument = async (documentId) => {
    const token = getToken();
    if (!token || !claimNumber) return;
    
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    setDeleting(documentId);
    
    try {
      const response = await fetch(`${API_URL}/api/documents/${claimNumber}/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete document');
      }
      
      // Refresh claim data
      fetchClaimDetails();
      
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setDeleting(null);
    }
  };

  // Handle document download
  const handleDownloadDocument = async (documentId, filename) => {
    const token = getToken();
    if (!token || !claimNumber) return;
    
    try {
      const response = await fetch(`${API_URL}/api/documents/${claimNumber}/${documentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download document');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      setUploadError(err.message);
    }
  };

  // Handle document preview
  const handlePreviewDocument = (doc, index) => {
    setPreviewDoc(doc);
    setPreviewIndex(index);
  };

  // Handle preview navigation
  const handlePreviewNavigate = (newIndex) => {
    const docs = claim?.documents || [];
    if (newIndex >= 0 && newIndex < docs.length) {
      setPreviewDoc(docs[newIndex]);
      setPreviewIndex(newIndex);
    }
  };

  // Check if file is previewable
  const isPreviewable = (filename) => {
    const ext = filename?.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'pdf'].includes(ext);
  };

  // Get file icon based on type
  const getFileIcon = (filename) => {
    const ext = filename?.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'heic', 'heif', 'webp'].includes(ext)) {
      return <Image className="h-5 w-5 text-blue-500" />;
    }
    if (ext === 'pdf') {
      return <FileText className="h-5 w-5 text-red-500" />;
    }
    if (['doc', 'docx'].includes(ext)) {
      return <FileText className="h-5 w-5 text-blue-600" />;
    }
    return <File className="h-5 w-5 text-slate-500" />;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Check if documents can be deleted (only in certain statuses)
  const canDeleteDocuments = ['submitted', 'pending_documents', 'under_review'].includes(claim?.status);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8EEFF] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-[#3B5BDB] mx-auto" />
          <p className="mt-4 text-slate-600 font-medium">Loading claim details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#E8EEFF] flex items-center justify-center px-4">
        <Card className="max-w-md w-full shadow-xl border-0 rounded-2xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              {error === 'Claim not found' ? 'Claim Not Found' : 'Something went wrong'}
            </h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button 
              onClick={() => navigate('/claims')} 
              className="bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white rounded-xl"
            >
              Back to Claims
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!claim) return null;

  const statusStyles = getStatusStyles(claim.status);
  const StatusIcon = getStatusIcon(claim.status);

  return (
    <PortalLayout pageTitle="Claim Details" userName={userName}>
      <div className="bg-[#E8EEFF] min-h-full py-8 md:py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          
          {/* Header */}
          <div className="mb-8">
            <button 
              onClick={() => navigate('/claims')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to Claims
            </button>
            
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-mono">
                  {claim.claim_number}
                </h1>
                <p className="text-slate-600 mt-1">
                  Filed on {formatDate(claim.submitted_at)}
                </p>
              </div>
              
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold ${statusStyles.bg} ${statusStyles.text}`}>
                <StatusIcon className="h-5 w-5" />
                {claim.status_label}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Action Required Alert */}
              {claim.status === 'pending_documents' && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <AlertCircle className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-amber-800">Action Required</h3>
                        <p className="text-amber-700 text-sm mt-1">
                          Additional documents are needed to process your claim. Please upload the requested documents to continue.
                        </p>
                        <Button className="mt-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl" size="sm">
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Documents
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Vehicle & Policy Card */}
              <Card className="shadow-xl border-0 rounded-2xl">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-[#E8EEFF] rounded-xl flex items-center justify-center">
                      <Car className="h-5 w-5 text-[#3B5BDB]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Covered Vehicle</h3>
                      <p className="text-sm text-slate-500">Policy: {claim.policy_number}</p>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 rounded-xl p-5">
                    <p className="text-xl font-bold text-slate-900 mb-3">
                      {claim.vehicle?.year} {claim.vehicle?.make} {claim.vehicle?.model}{claim.vehicle?.trim ? ` ${claim.vehicle.trim}` : ''}
                    </p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">VIN</p>
                        <p className="font-mono font-medium text-slate-700">{claim.vehicle?.vin || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Coverage Amount</p>
                        <p className="font-medium text-slate-700">{formatCurrency(claim.coverage_amount)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Deductible</p>
                        <p className="font-medium text-slate-700">{formatCurrency(claim.deductible_amount)}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Accident Details Card */}
              <Card className="shadow-xl border-0 rounded-2xl">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                      <AlertCircle className="h-5 w-5 text-red-500" />
                    </div>
                    <h3 className="font-semibold text-slate-900">Accident Details</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">Date of Accident</p>
                        <p className="font-medium text-slate-900 flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-slate-400" />
                          {formatDate(claim.accident_date)}
                        </p>
                      </div>
                      {claim.accident_location && (
                        <div>
                          <p className="text-sm text-slate-500">Location</p>
                          <p className="font-medium text-slate-900 flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-slate-400" />
                            {claim.accident_location}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Description</p>
                      <p className="text-slate-700 bg-slate-50 rounded-xl p-4">
                        {claim.accident_description}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-slate-500">Police Report</p>
                      <p className="font-medium text-slate-900">
                        {claim.police_report_available 
                          ? `Yes${claim.police_report_number ? ` - #${claim.police_report_number}` : ''}`
                          : 'Not filed'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* At-Fault Party Card */}
              {(claim.at_fault_party?.name || claim.at_fault_party?.insurance_company) && (
                <Card className="shadow-xl border-0 rounded-2xl">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                        <User className="h-5 w-5 text-purple-500" />
                      </div>
                      <h3 className="font-semibold text-slate-900">At-Fault Party</h3>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {claim.at_fault_party?.name && (
                        <div>
                          <p className="text-sm text-slate-500">Name</p>
                          <p className="font-medium text-slate-900">{claim.at_fault_party.name}</p>
                        </div>
                      )}
                      {claim.at_fault_party?.insurance_company && (
                        <div>
                          <p className="text-sm text-slate-500">Insurance Company</p>
                          <p className="font-medium text-slate-900">{claim.at_fault_party.insurance_company}</p>
                        </div>
                      )}
                      {claim.at_fault_party?.policy_number && (
                        <div>
                          <p className="text-sm text-slate-500">Policy Number</p>
                          <p className="font-medium text-slate-900">{claim.at_fault_party.policy_number}</p>
                        </div>
                      )}
                      {claim.at_fault_party?.claim_number && (
                        <div>
                          <p className="text-sm text-slate-500">Claim Number</p>
                          <p className="font-medium text-slate-900">{claim.at_fault_party.claim_number}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Repair Information Card */}
              <Card className="shadow-xl border-0 rounded-2xl">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                      <Wrench className="h-5 w-5 text-orange-500" />
                    </div>
                    <h3 className="font-semibold text-slate-900">Repair Information</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Repair Status</p>
                      <p className="font-medium text-slate-900 capitalize">
                        {claim.repair_status?.replace('_', ' ') || 'Not started'}
                      </p>
                    </div>
                    {claim.repair_shop_name && (
                      <div>
                        <p className="text-sm text-slate-500">Repair Shop</p>
                        <p className="font-medium text-slate-900">{claim.repair_shop_name}</p>
                      </div>
                    )}
                    {claim.estimated_repair_cost > 0 && (
                      <div>
                        <p className="text-sm text-slate-500">Estimated Repair Cost</p>
                        <p className="font-medium text-slate-900">{formatCurrency(claim.estimated_repair_cost)}</p>
                      </div>
                    )}
                    {claim.actual_repair_cost > 0 && (
                      <div>
                        <p className="text-sm text-slate-500">Actual Repair Cost</p>
                        <p className="font-medium text-slate-900">{formatCurrency(claim.actual_repair_cost)}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Documents Card */}
              <Card className="shadow-xl border-0 rounded-2xl">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Paperclip className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Documents</h3>
                        <p className="text-xs text-slate-500">{claim.documents?.length || 0} of 10 uploaded</p>
                      </div>
                    </div>
                    {(claim.documents?.length || 0) < 10 && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="rounded-xl"
                        onClick={() => setShowUploader(!showUploader)}
                        data-testid="add-document-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Document
                      </Button>
                    )}
                  </div>
                  
                  {/* Upload Error */}
                  {uploadError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                      <AlertCircle className="h-4 w-4 inline mr-2" />
                      {uploadError}
                      <button 
                        onClick={() => setUploadError(null)}
                        className="float-right text-red-500 hover:text-red-700"
                      >
                        <XCircle className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  
                  {/* Upload Area */}
                  {showUploader && (
                    <div className="mb-4">
                      <div 
                        className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-[#3B5BDB] transition-colors cursor-pointer"
                        onClick={() => document.getElementById('claim-doc-input')?.click()}
                      >
                        {uploading ? (
                          <Loader2 className="h-8 w-8 text-[#3B5BDB] animate-spin mx-auto mb-2" />
                        ) : (
                          <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                        )}
                        <p className="text-sm font-medium text-slate-700">
                          {uploading ? 'Uploading...' : 'Click to select files'}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">JPG, PNG, PDF, DOC up to 25 MB</p>
                        <input
                          id="claim-doc-input"
                          type="file"
                          multiple
                          accept=".jpg,.jpeg,.png,.heic,.heif,.webp,.pdf,.doc,.docx"
                          className="hidden"
                          disabled={uploading}
                          onChange={(e) => handleFileUpload(e.target.files)}
                        />
                      </div>
                      <button
                        onClick={() => setShowUploader(false)}
                        className="mt-2 text-sm text-slate-500 hover:text-slate-700"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  
                  {claim.documents?.length > 0 ? (
                    <div className="space-y-3">
                      {claim.documents.map((doc) => (
                        <div 
                          key={doc.document_id}
                          className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            {getFileIcon(doc.filename || doc.original_filename)}
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-900 truncate">{doc.filename || doc.original_filename}</p>
                              <p className="text-xs text-slate-500">
                                {doc.document_type_label || doc.document_type} 
                                {doc.file_size && ` · ${formatFileSize(doc.file_size)}`}
                                {doc.uploaded_at && ` · ${formatDateTime(doc.uploaded_at)}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {isPreviewable(doc.filename || doc.original_filename) && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handlePreviewDocument(doc, claim.documents?.indexOf(doc) || 0)}
                                className="text-slate-400 hover:text-blue-500"
                                title="Preview"
                                data-testid={`preview-doc-${doc.document_id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDownloadDocument(doc.document_id, doc.filename || doc.original_filename)}
                              title="Download"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {canDeleteDocuments && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteDocument(doc.document_id)}
                                disabled={deleting === doc.document_id}
                                className="text-slate-400 hover:text-red-500"
                                title="Delete"
                              >
                                {deleting === doc.document_id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    !showUploader && (
                      <div className="text-center py-8 bg-slate-50 rounded-xl">
                        <Paperclip className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-slate-500">No documents uploaded yet</p>
                        <p className="text-sm text-slate-400 mb-4">Upload supporting documents to strengthen your claim</p>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setShowUploader(true)}
                          className="rounded-xl"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Documents
                        </Button>
                      </div>
                    )
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              
              {/* Status Timeline Card */}
              <Card className="shadow-xl border-0 rounded-2xl">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-5">Claim Timeline</h3>
                  
                  <div className="space-y-4">
                    {timeline.map((entry, idx) => {
                      const EntryIcon = getStatusIcon(entry.status);
                      const entryStyles = getStatusStyles(entry.status);
                      const isLast = idx === timeline.length - 1;
                      
                      return (
                        <div key={idx} className="relative flex gap-4">
                          {!isLast && (
                            <div className="absolute left-5 top-10 w-0.5 h-full bg-slate-200" />
                          )}
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${entryStyles.bg}`}>
                            <EntryIcon className={`h-5 w-5 ${entryStyles.text}`} />
                          </div>
                          <div className="flex-1 pb-4">
                            <p className="font-medium text-slate-900">{entry.status_label}</p>
                            <p className="text-sm text-slate-500">{formatDateTime(entry.timestamp)}</p>
                            {entry.note && (
                              <p className="text-sm text-slate-600 mt-1 bg-slate-50 rounded-lg p-2">
                                {entry.note}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Claim Amounts Card */}
              {(claim.claimed_amount > 0 || claim.approved_amount > 0) && (
                <Card className="shadow-xl border-0 rounded-2xl">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-slate-900 mb-4">Claim Amounts</h3>
                    
                    <div className="space-y-3">
                      {claim.claimed_amount > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">Claimed</span>
                          <span className="font-semibold">{formatCurrency(claim.claimed_amount)}</span>
                        </div>
                      )}
                      {claim.approved_amount > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">Approved</span>
                          <span className="font-semibold text-green-600">{formatCurrency(claim.approved_amount)}</span>
                        </div>
                      )}
                      {claim.paid_amount > 0 && (
                        <div className="flex justify-between items-center pt-3 border-t border-slate-200">
                          <span className="text-slate-900 font-medium">Paid</span>
                          <span className="font-bold text-green-600">{formatCurrency(claim.paid_amount)}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Need Help Card */}
              <Card className="shadow-xl border-0 rounded-2xl bg-gradient-to-br from-[#EFF6FF] to-[#E8EEFF]">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Need Help?</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Our claims team is here to assist you with your claim.
                  </p>
                  <div className="space-y-3">
                    <a 
                      href="tel:18335677729"
                      className="flex items-center gap-3 text-slate-700 hover:text-[#3B5BDB] transition-colors"
                    >
                      <Phone className="h-4 w-4" />
                      <span className="font-medium">1-833-LOSSPAY</span>
                    </a>
                    <a 
                      href="mailto:claims@losspay.com"
                      className="flex items-center gap-3 text-slate-700 hover:text-[#3B5BDB] transition-colors"
                    >
                      <Mail className="h-4 w-4" />
                      <span className="font-medium">claims@losspay.com</span>
                    </a>
                  </div>
                </CardContent>
              </Card>

              {/* Refresh Button */}
              <Button
                variant="outline"
                onClick={fetchClaimDetails}
                className="w-full rounded-xl"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Status
              </Button>
            </div>
          </div>

        </div>
      </div>

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        isOpen={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        document={previewDoc}
        claimNumber={claimNumber}
        token={getToken()}
        documents={claim?.documents || []}
        currentIndex={previewIndex}
        onNavigate={handlePreviewNavigate}
      />
    </PortalLayout>
  );
}
